import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import clsx from "clsx";
import { ChevronDown } from "lucide-react";

interface Option { value: string; label: string; }
interface Props extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "children"> {
  label?: string;
  hint?: string;
  error?: string;
  options: Option[];
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, Props>(function Select(
  { label, hint, error, options, placeholder, className, id, ...rest }, ref,
) {
  const sid = id ?? rest.name;
  return (
    <div className="w-full">
      {label && (
        <label htmlFor={sid} className="block text-sm font-medium text-surface-800 mb-1.5">
          {label}
        </label>
      )}
      <div className="relative">
        <select
          ref={ref}
          id={sid}
          className={twMerge(clsx(
            "w-full h-10 pl-3.5 pr-10 bg-white border border-surface-300 rounded-lg text-surface-900",
            "appearance-none transition-colors",
            "hover:border-surface-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/40",
            "disabled:bg-surface-50 disabled:cursor-not-allowed",
            error && "border-danger-500",
            className,
          ))}
          {...rest}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-surface-400 pointer-events-none" />
      </div>
      {error && <p className="mt-1.5 text-xs text-danger-600">{error}</p>}
      {hint && !error && <p className="mt-1.5 text-xs text-surface-500">{hint}</p>}
    </div>
  );
});
